import {DataContentWrapper} from '@hconnect/common/containers'
import {type Iso8601} from '@hconnect/common/types'
import {CardBox} from '@hconnect/uikit'
import {CalendarTodayOutlined} from '@mui/icons-material'
import {Box, SxProps, Theme, Typography, Chip} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {ExpertSystemEquipmentCharts} from '../components'
import {EXPERT_SYSTEM_WIDGET_NAMESPACE} from '../consts'
import {useExpertSystemEquipmentsWithNames} from '../hooks'

type Props = {
  hierarchyId: string
  from?: Iso8601
  to?: Iso8601
  timeRangeLabel?: string
  cardBoxSx?: SxProps<Theme>
  placeholderWrapperSx?: SxProps<Theme>
}

const cardBoxPx = {xs: 2, md: 3}

export const ExpertSystemUsageContainer: React.FC<Props> = ({
  hierarchyId,
  from,
  to,
  timeRangeLabel,
  cardBoxSx,
  placeholderWrapperSx
}) => {
  const {t} = useTranslation(EXPERT_SYSTEM_WIDGET_NAMESPACE)
  const isTimeRangeSupported = !!from && !!to

  const {data, isLoading, isError, isRefetching, refetch} = useExpertSystemEquipmentsWithNames(
    {hierarchyId, from, to},
    {enabled: isTimeRangeSupported}
  )

  return (
    <CardBox
      sx={{
        ...cardBoxSx,
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        px: 0,
        pl: cardBoxPx
      }}
      data-test-id="expert-system-card"
    >
      <Box
        display="flex"
        flexDirection="row"
        sx={{
          display: 'flex',
          pr: cardBoxPx
        }}
      >
        <Box flex={1}>
          <Typography variant="h3" mb={2}>
            {t('expertSystemUtilization.cardTitle')}
          </Typography>
        </Box>
        {timeRangeLabel && (
          <Chip icon={<CalendarTodayOutlined />} label={timeRangeLabel} size="small" sx={{mx: 1}} />
        )}
      </Box>
      <DataContentWrapper
        showLoader={isLoading}
        isError={isError}
        isRefetching={isRefetching}
        data={data}
        retryFunction={() => refetch()}
        progressSx={{color: 'primary.main'}}
        placeholderWrapperSx={placeholderWrapperSx}
        noDataSettings={{
          ...(!isTimeRangeSupported && {
            noDataMessage: t('expertSystemUtilization.message.unsupportedTimeRange')
          })
        }}
      >
        {data && (
          <Box
            sx={{display: 'flex', flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: cardBoxPx}}
          >
            <ExpertSystemEquipmentCharts expertSystemEquipments={data} />
          </Box>
        )}
      </DataContentWrapper>
    </CardBox>
  )
}
